import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { bookMeeting, fetchPartners, fetchAvailability } from "../services/api";

export default function SchedulerPage() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPartner, setSelectedPartner] = useState(null);
  
  //  Initialize with today's date in YYYY-MM-DD format (Local Time)
  const localToday = new Date().toLocaleDateString('en-CA'); 
  const [selectedDate, setSelectedDate] = useState(localToday);
  
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState(null);
  
  const [availability, setAvailability] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  // Fetch partners on mount
  useEffect(() => {
    const loadPartners = async () => {
      try {
        const data = await fetchPartners();
        setPartners(data);
      } catch (err) {
        console.error("Failed to load partners:", err);
      } finally {
        setLoading(false);
      }
    };
    loadPartners();
  }, []);

  // Dynamic Fetch: Fetches a 30-day window whenever a partner is selected
  useEffect(() => {
    if (selectedPartner) {
      setSlotsLoading(true);
      setAvailability([]);
      
      const now = Math.floor(Date.now() / 1000);
      const startUnix = now - (now % 300); 

      const endUnix = startUnix + (86400 * 30); 

      fetchAvailability(selectedPartner.grantId, selectedPartner.email, startUnix, endUnix)
        .then(slots => {
          const formattedSlots = slots.map(slot => ({
            start: new Date((slot.startTime || slot.start_time) * 1000),
            end: new Date((slot.endTime || slot.end_time) * 1000)
          }));
          setAvailability(formattedSlots);
        })
        .catch(err => {
          console.error("Availability Fetch Error:", err);
          setAvailability([]);
        })
        .finally(() => setSlotsLoading(false));
    }
  }, [selectedPartner]);

  // Timezone-Safe Filtering: Compare local date strings
  const dailySlots = availability.filter(slot => {
    const slotLocalDate = slot.start.toLocaleDateString('en-CA'); 
    return slotLocalDate === selectedDate;
  });

  const filteredPartners = partners.filter((p) =>
    `${p.name} ${p.company || ''}`.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f2f3f3]">
        <div className="text-xl font-bold text-[#232f3e] animate-pulse underline decoration-[#ff9900]">
          Initializing Partner Central...
        </div>
      </div>
    );
  }

 
  return (
    <div className="min-h-screen bg-[#f2f3f3] p-6 md:p-12 font-sans text-[#232f3e]">
      <div className="max-w-6xl mx-auto space-y-8">
        
        {/* Header */}
        <div className="border-l-4 border-[#ff9900] pl-6">
          <h1 className="text-3xl font-bold tracking-tight">Partner Central Scheduler</h1>
          <p className="text-[#545b64] mt-1">Select a verified partner to coordinate technical sessions.</p>
        </div>

        {/* Partner Selection Grid */}
        <section className="bg-white shadow-sm border border-[#eaeded] p-6 rounded-sm">
          <div className="mb-6">
            <label className="block text-xs font-bold uppercase text-[#545b64] mb-2 tracking-wider">Search Partners</label>
            <input
              className="w-full max-w-md px-4 py-2 border border-[#879196] focus:border-[#ff9900] focus:ring-1 focus:ring-[#ff9900] outline-none transition-all"
              placeholder="Filter by name, company, or role..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredPartners.map((partner) => (
              <div
                key={partner.email}
                onClick={() => setSelectedPartner(partner)}
                className={`p-4 border cursor-pointer transition-all ${
                  selectedPartner?.email === partner.email
                    ? "border-[#ff9900] bg-[#fffaf0] ring-1 ring-[#ff9900] shadow-sm"
                    : "border-[#eaeded] bg-white hover:border-[#879196]"
                }`}
              >
                <div className="font-bold text-lg">{partner.name}</div>
                <div className="text-[#0073bb] text-sm font-semibold">{partner.company || "Independent Partner"}</div>
                <div className="text-[#545b64] text-xs mt-1 italic">{partner.role || "Technical Consultant"}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Availability Section */}
        <AnimatePresence mode="wait">
          {selectedPartner && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="grid grid-cols-1 md:grid-cols-12 gap-6"
            >
              {/* Date Picker */}
            <div className="md:col-span-4 bg-white p-6 border border-[#eaeded] shadow-sm">
              <h3 className="text-sm font-bold mb-4 uppercase text-[#545b64]">Step 1: Choose Date</h3>
              <input 
                type="date" 
                min={localToday} 
                value={selectedDate} 
                onChange={(e) => setSelectedDate(e.target.value)} 
                className="w-full p-2 border border-[#879196] outline-none focus:border-[#0073bb] font-medium" 
              />
              
              {/* Timezone Indicator */}
              <div className="mt-4 p-3 bg-[#f3faff] border border-[#0073bb]/20 rounded-sm">
                <p className="text-[10px] font-bold text-[#0073bb] uppercase tracking-tighter">Your Timezone</p>
                <p className="text-xs font-bold text-[#232f3e]">
                  {Intl.DateTimeFormat().resolvedOptions().timeZone}
                </p>
                <p className="text-[10px] text-[#545b64] mt-1 italic">
                  Please note:Slots are automatically adjusted to your local clock.
                </p>
              </div>
            </div>

              {/* Slots Grid */}
              <div className="md:col-span-8 bg-white p-6 border border-[#eaeded] shadow-sm min-h-[250px]">
                <h3 className="text-sm font-bold mb-4 uppercase text-[#545b64]">Step 2: Available 30-Min Slots</h3>
                
                {slotsLoading ? (
                  <div className="flex flex-col items-center justify-center h-32 space-y-2">
                    <div className="w-8 h-8 border-4 border-[#ff9900] border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-sm font-bold text-[#545b64]">Syncing with Nylas API...</span>
                  </div>
                ) : dailySlots.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {dailySlots.map((slot, i) => (
                      <button 
                        key={i} 
                        onClick={() => setSelectedSlot(slot)} 
                        className="py-2 px-3 border-2 border-[#ff9900] text-[#232f3e] hover:bg-[#232f3e] hover:text-white hover:border-[#232f3e] text-sm font-bold transition-all"
                      >
                        {/* Automatically displays in user's local time */}
                        {slot.start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-32 border-2 border-dashed border-[#eaeded] text-sm text-[#879196] italic text-center px-4">
                    No sessions available for {new Date(selectedDate + "T00:00:00").toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Modal Logic */}
        {selectedSlot && (
          <BookingModal
            slot={selectedSlot}
            partner={selectedPartner}
            onClose={() => setSelectedSlot(null)}
            onSuccess={(msg) => {
              setAvailability(prev => prev.filter(s => s.start.getTime() !== selectedSlot.start.getTime()));              setToast(msg);
              setTimeout(() => setToast(null), 6000);
            }}
          />
        )}

        {/* Success Toast */}
        <AnimatePresence>
          {toast && (
            <motion.div 
              initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
              className="fixed bottom-8 right-8 bg-[#232f3e] text-white px-8 py-4 border-l-8 border-[#ff9900] shadow-2xl z-[200] flex items-center space-x-4"
            >
              <span className="font-bold">{toast}</span>
              <button onClick={() => setToast(null)} className="text-[#879196] hover:text-white">✕</button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function BookingModal({ slot, partner, onClose, onSuccess }) {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", attendees: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const guestEmailArray = formData.attendees
      ? formData.attendees.split(",").map((email) => email.trim()).filter(Boolean)
      : [];

    try {
      const startUnix = Math.floor(slot.start.getTime() / 1000);
      const endUnix = Math.floor(slot.end.getTime() / 1000);

      await bookMeeting({
        partnerGrantId: partner.grantId,
        userEmail: formData.email,
        guestEmails: guestEmailArray,
        startTime: startUnix,
        endTime: endUnix,
        title: `AWS Partner Session: ${formData.name} x ${partner.name}`,
        description: `Company Contact: ${formData.phone}`
      });

      onSuccess(`Request Sent! Partner notified for ${slot.start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`);
      onClose();
    } catch (err) {
      setError(err.message || "Scheduling conflict. Please try another slot.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#232f3e]/80 backdrop-blur-sm">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white p-8 w-full max-w-lg shadow-2xl border-t-8 border-[#ff9900]">
        <h2 className="text-2xl font-bold mb-1 text-[#232f3e]">Review & Confirm</h2>
        <p className="text-sm text-[#545b64] mb-6">Partner: <span className="font-bold text-[#0073bb]">{partner.name}</span></p>

        {error && <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-xs font-bold">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black uppercase text-[#545b64] mb-1">Your Name</label>
              <input required className="w-full px-3 py-2 border border-[#879196] outline-none focus:border-[#0073bb]" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase text-[#545b64] mb-1">Your Email</label>
              <input required type="email" className="w-full px-3 py-2 border border-[#879196] outline-none focus:border-[#0073bb]" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-black uppercase text-[#545b64] mb-1">Phone</label>
            <input required type="tel" className="w-full px-3 py-2 border border-[#879196] outline-none focus:border-[#0073bb]" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
          </div>
          <div>
            <label className="block text-[10px] font-black uppercase text-[#545b64] mb-1">Invite Collaborators (CSV)</label>
            <textarea className="w-full px-3 py-2 border border-[#879196] outline-none focus:border-[#0073bb]" rows="2" value={formData.attendees} onChange={e => setFormData({...formData, attendees: e.target.value})} />
          </div>
          <div className="flex gap-4 pt-4">
            <button type="button" className="flex-1 py-3 text-sm font-bold text-[#545b64] hover:bg-[#eaeded]" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={loading} className="flex-[2] py-3 bg-[#ff9900] hover:bg-[#ec7211] text-white font-bold text-sm shadow-md disabled:opacity-50">
              {loading ? "Transmitting to AWS..." : "Confirm Technical Session"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}